package org.moxultimate.topwithmox.core.base

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel

/**
 * Lightweight ViewModel base class for Kobweb (Kotlin/JS).
 *
 * Mirrors the Android ViewModel contract without the Android lifecycle dependency:
 * - Provides [viewModelScope] backed by [Dispatchers.Main] + [SupervisorJob].
 * - [clear] cancels the scope and calls [onCleared] — triggered automatically
 *   by [rememberViewModel] when the composable leaves the composition.
 *
 * Usage:
 * ```kotlin
 * class CounterViewModel : ViewModel() {
 *     private val _state = MutableStateFlow(CounterUiState())
 *     val state: StateFlow<CounterUiState> = _state.asStateFlow()
 *
 *     fun increment() {
 *         viewModelScope.launch { _state.update { it.copy(count = it.count + 1) } }
 *     }
 * }
 * ```
 */
abstract class ViewModel {

    /**
     * Coroutine scope tied to this ViewModel's lifetime.
     * All launched coroutines are cancelled when [clear] is called.
     */
    val viewModelScope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    /**
     * Called when the ViewModel is about to be destroyed.
     * Override to release resources (e.g., close streams, cancel subscriptions).
     * The [viewModelScope] is cancelled immediately after this returns.
     */
    protected open fun onCleared() {}

    /** Cancels [viewModelScope] and calls [onCleared]. Called by [rememberViewModel]. */
    fun clear() {
        onCleared()
        viewModelScope.cancel()
    }
}

/**
 * Creates and remembers a [ViewModel] instance scoped to the current composable.
 *
 * The ViewModel is created once on first composition and destroyed (via [ViewModel.clear])
 * when the composable permanently leaves the tree. It survives recompositions.
 *
 * ```kotlin
 * @Composable
 * fun CounterScreen() {
 *     val viewModel = rememberViewModel { CounterViewModel() }
 *     val state by viewModel.state.collectAsState()
 * }
 * ```
 *
 * @param factory Lambda that constructs the ViewModel. Called exactly once.
 */
@Composable
inline fun <reified VM : ViewModel> rememberViewModel(crossinline factory: () -> VM): VM {
    val viewModel = remember { factory() }
    DisposableEffect(viewModel) {
        onDispose { viewModel.clear() }
    }
    return viewModel
}
